import React, { useEffect, useState } from "react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import SettingsHeader from "./SettingsHeader";
import { Link, useLocation } from "react-router-dom";

import { ChevronRight, Mail } from "lucide-react";
import useIndiaLocation from "../../hooks/useIndiaLocation";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phoneAreaCode: "",
  phoneNumber: "",
  preferredContact: "",
  details: "",
};

const ContactUs = () => {
  const location = useLocation();
  const data = location.state || {};
  const isIndiaLocation = useIndiaLocation();
  const [formData, setFormData] = useState(initialFormData);

  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
    setFormErrors((current) => ({ ...current, [name]: "" }));
    setSuccess(false);
    setError(null);
  };

  const validateEmail = (email) => {
    if (email.includes("@@")) {
      return false;
    }

    if (email.includes("..")) {
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return emailRegex.test(email);
  };

  const validatePhoneSegment = (value) => /^[0-9+\-\s()]+$/.test(value.trim());

  const validateName = (name, minimumLength = 2) => {
    const trimmed = name.trim();
    const invalidCharRegex = /[^a-zA-Z\s]/;
    return trimmed.length >= minimumLength && !invalidCharRegex.test(trimmed);
  };

  const validateForm = () => {
    const errors = {};

    if (!validateName(formData.firstName)) {
      errors.firstName =
        "Please enter a valid first name with at least 2 letters.";
    }

    if (formData.lastName.trim() && !validateName(formData.lastName)) {
      errors.lastName =
        "Please enter a valid last name using letters only.";
    }

    if (!formData.email || !validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }

    if (!formData.preferredContact) {
      errors.preferredContact = "Please choose how you would like us to contact you.";
    }

    if (formData.phoneAreaCode.trim() && !validatePhoneSegment(formData.phoneAreaCode)) {
      errors.phoneAreaCode = "Use only digits and standard phone symbols.";
    }

    if (formData.phoneNumber.trim() && !validatePhoneSegment(formData.phoneNumber)) {
      errors.phoneNumber = "Use only digits and standard phone symbols.";
    }

    if (
      formData.preferredContact === "Phone Call" &&
      !formData.phoneNumber.trim()
    ) {
      errors.phoneNumber = "Phone number is required if you prefer a phone call.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fullName = [formData.firstName, formData.lastName]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(" ");
      const phoneNumber = [formData.phoneAreaCode, formData.phoneNumber]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(" ");
      const messageParts = [
        "Enquiry type: Demo or pricing discussion",
        `Preferred mode of contact: ${formData.preferredContact || "Not specified"}`,
        phoneNumber ? `Phone number: ${phoneNumber}` : null,
        formData.details.trim() ? `Details: ${formData.details.trim()}` : null,
      ].filter(Boolean);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/contectUs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
          },
          body: JSON.stringify({
            Name: fullName || formData.firstName.trim(),
            Email: formData.email.trim(),
            Message: messageParts.join("\n"),
          }),
        }
      );

      if (response.status === 200) {
        setSuccess(true);
        setFormData(initialFormData);
        setFormErrors({});
      } else {
        throw new Error("Failed to send message");
      }
    } catch (err) {
      setError(err.message || "An error occurred while sending your message");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="contact-page settings-page min-h-screen flex flex-col items-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <SettingsHeader name="Contact us" oneBack fallbackBackTo="/" />
      {data.public ?? (
        <div className="contact-breadcrumb flex gap-2 mb-6 absolute left-12 top-28">
          <Link to={`/settings`}>Settings</Link>
          <ChevronRight />
          <p className="text-gray-700">Contact us</p>
        </div>
      )}

      <div className="contact-content my-14 w-full px-4 md:px-12 lg:px-20">
        <p className="contact-intro mx-auto mb-8 mt-2 max-w-3xl text-center text-base text-[#374151] md:text-lg">
          Need help with demos, pricing, or product questions? Share a few
          details below and our team will reach out with the right next step.
        </p>

        <div className="contact-layout mx-auto flex w-full max-w-7xl flex-col gap-8 md:flex-row md:items-start">
          <div className="contact-info-card flex w-full min-h-[380px] flex-col rounded-[28px] bg-lime-300 px-7 pb-7 pt-7 shadow-[0_18px_40px_rgba(15,23,42,0.08)] md:w-[340px] md:min-h-[560px] md:flex-none lg:w-[380px] lg:min-h-[620px]">
            <div>
              <h2 className="contact-info-title mb-2 text-[1.75rem] font-bold leading-tight text-[#1f2937]">
                Contact information
              </h2>
              <p className="contact-info-copy mb-7 text-[15px] leading-6 text-[#374151]">
                Fill up the form and our team will get back to you within 24
                hours
              </p>

              <div className="contact-email-row flex items-center rounded-2xl bg-white/45 px-4 py-3 text-[15px] font-medium text-[#1f2937]">
                <Mail className="mr-4" size={20} />
                <span>support@mathdingo.com</span>
              </div>
            </div>

            {!isIndiaLocation ? (
              <p className="contact-info-footer mt-auto border-t border-[#1f2937]/20 pt-5 text-[15px] leading-6 text-[#374151]">
                Australian-built, thoughtfully designed, and made to last.
              </p>
            ) : null}
          </div>

          <div className="contact-form-column w-full md:min-w-0 md:flex-1">
            <div className="contact-form-card overflow-hidden rounded-[32px] border border-[#ddd7cf] bg-white shadow-[0_24px_60px_rgba(15,23,42,0.12)] md:flex md:min-h-[980px] md:flex-col">
              <div className="contact-form-header border-b border-[#ede7df] bg-[#fcfaf7] px-6 py-5 md:px-8">
                <h2 className="contact-form-title text-2xl font-black text-[#1f2937] md:text-[2rem]">
                  Request a demo or pricing discussion
                </h2>
                <p className="contact-form-subtitle mt-2 text-sm text-[#5b6472] md:text-base">
                  Complete the form below and our team will review your enquiry.
                </p>
              </div>

              <div
                className="contact-brand-panel relative overflow-hidden px-6 py-6 md:px-8"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(135deg, #f7c843 0%, #f3b22d 100%)",
                  backgroundSize: "22px 22px, 22px 22px, cover",
                }}
              >
                <div className="absolute -left-8 top-4 h-24 w-24 rounded-full bg-white/20 blur-2xl" />
                <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-[#172554]/15 blur-3xl" />
                <div className="relative z-10 flex flex-col items-center text-center">
                  <img
                    src={GLOBAL_IMAGES.dingoLogoText}
                    alt="MathDingo"
                    className="h-10 w-auto"
                  />
                  <h3 className="contact-brand-title mt-4 whitespace-nowrap text-center text-[1.9rem] font-black uppercase leading-tight text-[#172554] md:text-[2.4rem]">
                    A math app for kids
                  </h3>
                </div>
              </div>

              <div className="contact-query-panel bg-[#434148] px-6 py-7 text-center md:px-8">
                <h3 className="contact-query-title text-[1.9rem] font-black text-[#f7c843]">
                  Got any queries?
                </h3>
                <p className="contact-query-copy mt-2 text-sm text-white/85 md:text-base">
                  Fill out the form below and we&apos;ll get back to you asap.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="contact-form space-y-6 px-6 py-7 md:flex-1 md:px-8 md:py-8"
              >
                <div>
                  <label
                    htmlFor="contact-first-name"
                    className="mb-3 block text-sm font-black tracking-[0.04em] text-[#3f4654]"
                  >
                    Your name <span className="text-[#dc2626]">*</span>
                  </label>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <input
                        id="contact-first-name"
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition ${
                          formErrors.firstName
                            ? "border-red-500"
                            : "border-[#d1d5db] focus:border-[#172554]"
                        }`}
                        required
                      />
                      <p className="mt-2 text-xs text-[#6b7280]">First Name</p>
                      {formErrors.firstName ? (
                        <p className="mt-1 text-sm text-red-500">
                          {formErrors.firstName}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <input
                        id="contact-last-name"
                        name="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition ${
                          formErrors.lastName
                            ? "border-red-500"
                            : "border-[#d1d5db] focus:border-[#172554]"
                        }`}
                      />
                      <p className="mt-2 text-xs text-[#6b7280]">Last Name</p>
                      {formErrors.lastName ? (
                        <p className="mt-1 text-sm text-red-500">
                          {formErrors.lastName}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="mb-3 block text-sm font-black tracking-[0.04em] text-[#3f4654]"
                  >
                    Email address <span className="text-[#dc2626]">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition ${
                      formErrors.email
                        ? "border-red-500"
                        : "border-[#d1d5db] focus:border-[#172554]"
                    }`}
                    required
                  />
                  <p className="mt-2 text-xs text-[#6b7280]">
                    example@example.com
                  </p>
                  {formErrors.email ? (
                    <p className="mt-1 text-sm text-red-500">
                      {formErrors.email}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="contact-phone-area"
                    className="mb-3 block text-sm font-black tracking-[0.04em] text-[#3f4654]"
                  >
                    Phone number
                  </label>
                  <div className="grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
                    <div>
                      <input
                        id="contact-phone-area"
                        name="phoneAreaCode"
                        type="text"
                        value={formData.phoneAreaCode}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition ${
                          formErrors.phoneAreaCode
                            ? "border-red-500"
                            : "border-[#d1d5db] focus:border-[#172554]"
                        }`}
                      />
                      <p className="mt-2 text-xs text-[#6b7280]">Area Code</p>
                      {formErrors.phoneAreaCode ? (
                        <p className="mt-1 text-sm text-red-500">
                          {formErrors.phoneAreaCode}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <input
                        id="contact-phone-number"
                        name="phoneNumber"
                        type="text"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition ${
                          formErrors.phoneNumber
                            ? "border-red-500"
                            : "border-[#d1d5db] focus:border-[#172554]"
                        }`}
                      />
                      <p className="mt-2 text-xs text-[#6b7280]">
                        Phone Number
                      </p>
                      {formErrors.phoneNumber ? (
                        <p className="mt-1 text-sm text-red-500">
                          {formErrors.phoneNumber}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="mb-3 block text-sm font-black tracking-[0.04em] text-[#3f4654]">
                    Preferred mode of contact{" "}
                    <span className="text-[#dc2626]">*</span>
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {["Email", "Phone Call"].map((option) => (
                      <label
                        key={option}
                        className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                          formData.preferredContact === option
                            ? "border-[#172554] bg-[#eef2ff] text-[#172554]"
                            : "border-[#d1d5db] bg-white text-[#4b5563]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="preferredContact"
                          value={option}
                          checked={formData.preferredContact === option}
                          onChange={handleChange}
                          className="h-4 w-4 accent-[#172554]"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  {formErrors.preferredContact ? (
                    <p className="mt-2 text-sm text-red-500">
                      {formErrors.preferredContact}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="contact-details"
                    className="mb-3 block text-sm font-black tracking-[0.04em] text-[#3f4654]"
                  >
                    Details
                  </label>
                  <textarea
                    id="contact-details"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    rows={5}
                    className="w-full rounded-xl border border-[#d1d5db] bg-white px-4 py-3.5 text-[15px] text-[#1f2937] shadow-sm outline-none transition focus:border-[#172554]"
                  />
                  <p className="mt-2 text-xs text-[#6b7280]">
                    Let us know your questions about the product or what you
                    would like to discuss.
                  </p>
                </div>

                {error ? <div className="text-sm text-red-500">{error}</div> : null}
                {success ? (
                  <div className="text-sm font-semibold text-green-600">
                    Enquiry sent successfully. We&apos;ll be in touch soon.
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex w-full items-center justify-center rounded-full bg-[#172554] px-8 py-3.5 text-base font-black text-white transition hover:bg-[#101b3f] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                  >
                    {isLoading ? "Submitting..." : "Submit enquiry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
